/** Редактор классического сообщения: текст, эмбеды, компоненты, опрос. */
import { cloneWithNewIds, newEmbed, newField, newPoll, newRow, uid } from '../../../src/shared/factory.js';
import { LIMITS } from '../../../src/shared/limits.js';
import { embedLength } from '../../../src/shared/validate.js';
import type { DEmbed } from '../../../src/shared/types.js';
import { h, hexColor, icon } from '../dom.js';
import { store } from '../store.js';
import { colorField, emojiField, mediaField, numberField, selectField, switchField, textField } from '../ui/inputs.js';
import { addButton, emptyHint, group, itemCard, listActions, row, sectionPanel } from './common.js';
import { rowEditor } from './components.js';

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function timestampField(e: DEmbed): HTMLElement {
  const input = h('input', { class: 'input', type: 'datetime-local', value: toLocalInput(e.timestamp), attrs: { 'aria-label': 'Время в футере' } });
  input.addEventListener('input', () => store.input(`${e.id}.ts`, () => (e.timestamp = input.value ? new Date(input.value).toISOString() : null)));
  const now = h(
    'button',
    {
      class: 'btn btn-sm',
      type: 'button',
      onclick: () => {
        const iso = new Date().toISOString();
        input.value = toLocalInput(iso);
        store.input(`${e.id}.ts`, () => (e.timestamp = iso));
      },
    },
    'Сейчас',
  );
  const clear = h(
    'button',
    {
      class: 'icon-btn',
      type: 'button',
      title: 'Убрать время',
      onclick: () => {
        input.value = '';
        store.input(`${e.id}.ts`, () => (e.timestamp = null));
      },
    },
    icon('x', 14),
  );
  return h('div', { class: 'field' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, 'Время в футере')), h('div', { class: 'input-group' }, input, now, clear));
}

function embedCard(e: DEmbed, i: number): HTMLElement {
  const d = store.draft;
  const used = embedLength(e);
  return itemCard({
    id: e.id,
    icon: 'layers',
    accent: e.color === null ? null : hexColor(e.color),
    title: `Эмбед ${i + 1}`,
    subtitle: e.title || e.author.name || (e.description ? e.description.slice(0, 60) : 'пустой'),
    badge: `${used} симв.`,
    actions: listActions(d.embeds, i, (x) => cloneWithNewIds(x)),
    body: () => [
      row(
        colorField('Цвет полосы', e.color, (v) => store.input(`${e.id}.color`, () => (e.color = v))),
        timestampField(e),
      ),
      group(
        'Автор',
        row(
          textField({ label: 'Имя автора', value: e.author.name, max: LIMITS.authorName, onInput: (v) => store.input(`${e.id}.an`, () => (e.author.name = v)) }),
          textField({ label: 'Ссылка автора', value: e.author.url, placeholder: 'https://', onInput: (v) => store.input(`${e.id}.au`, () => (e.author.url = v)) }),
        ),
        mediaField('Иконка автора', e.author.iconUrl, (v) => store.input(`${e.id}.ai`, () => (e.author.iconUrl = v))),
      ),
      group(
        'Заголовок и описание',
        textField({ label: 'Заголовок', value: e.title, max: LIMITS.embedTitle, toolbar: 'inline', onInput: (v) => store.input(`${e.id}.title`, () => (e.title = v)) }),
        textField({ label: 'Ссылка заголовка', value: e.url, placeholder: 'https://', hint: 'Одинаковая ссылка у нескольких эмбедов объединяет их картинки в галерею (до 4)', onInput: (v) => store.input(`${e.id}.url`, () => (e.url = v)) }),
        textField({ label: 'Описание', value: e.description, max: LIMITS.embedDescription, multiline: true, rows: 4, toolbar: 'full', onInput: (v) => store.input(`${e.id}.desc`, () => (e.description = v)) }),
      ),
      group(
        `Поля (${e.fields.length}/${LIMITS.embedFields})`,
        e.fields.length
          ? h(
              'div',
              { class: 'nested-list' },
              e.fields.map((f, j) =>
                itemCard({
                  id: f.id,
                  level: 1,
                  icon: f.inline ? 'grid' : 'menu',
                  title: f.name || `Поле ${j + 1}`,
                  subtitle: f.inline ? 'в строку' : 'на всю ширину',
                  defaultCollapsed: e.fields.length > 4,
                  actions: listActions(e.fields, j, (x) => cloneWithNewIds(x)),
                  body: () => [
                    textField({ label: 'Название', value: f.name, max: LIMITS.fieldName, toolbar: 'inline', required: true, onInput: (v) => store.input(`${f.id}.n`, () => (f.name = v)) }),
                    textField({ label: 'Значение', value: f.value, max: LIMITS.fieldValue, multiline: true, rows: 2, toolbar: 'full', required: true, onInput: (v) => store.input(`${f.id}.v`, () => (f.value = v)) }),
                    switchField('В строку (inline)', f.inline, (v) => store.input(`${f.id}.i`, () => (f.inline = v)), 'До 3 полей в ряд, с миниатюрой - до 2'),
                  ],
                }),
              ),
            )
          : null,
        h(
          'div',
          { class: 'btn-row' },
          addButton('Поле', () => store.change(() => e.fields.push(newField())), { small: true, disabled: e.fields.length >= LIMITS.embedFields }),
          addButton(
            'Пустое поле-разделитель',
            () => store.change(() => e.fields.push({ id: uid(), name: '​', value: '​', inline: true })),
            { small: true, disabled: e.fields.length >= LIMITS.embedFields },
          ),
        ),
      ),
      group(
        'Картинки',
        mediaField('Большое изображение', e.image, (v) => store.input(`${e.id}.img`, () => (e.image = v))),
        mediaField('Миниатюра (справа)', e.thumbnail, (v) => store.input(`${e.id}.thumb`, () => (e.thumbnail = v))),
      ),
      group(
        'Футер',
        textField({ label: 'Текст футера', value: e.footer.text, max: LIMITS.footerText, onInput: (v) => store.input(`${e.id}.ft`, () => (e.footer.text = v)) }),
        mediaField('Иконка футера', e.footer.iconUrl, (v) => store.input(`${e.id}.fi`, () => (e.footer.iconUrl = v))),
      ),
    ],
  });
}

export function classicEditor(): HTMLElement[] {
  const d = store.draft;
  const totalEmbed = d.embeds.reduce((s, e) => s + embedLength(e), 0);
  return [
    sectionPanel({
      id: 'sec-content',
      title: 'Текст сообщения',
      icon: 'text',
      body: () => [
        textField({
          value: d.content,
          max: LIMITS.content,
          label: 'Текст',
          multiline: true,
          rows: 3,
          toolbar: 'full',
          placeholder: 'Поддерживается разметка Discord: **жирный**, # заголовки, > цитаты, списки, ||спойлеры||, <t:1700000000:R>...',
          onInput: (v) => store.input('content', () => (d.content = v)),
        }),
      ],
    }),
    sectionPanel({
      id: 'sec-embeds',
      title: 'Эмбеды',
      icon: 'layers',
      badge: `${d.embeds.length}/${LIMITS.embedsPerMessage} · ${totalEmbed}/${LIMITS.embedTotal}`,
      body: () => [
        d.embeds.length ? h('div', { class: 'list' }, d.embeds.map(embedCard)) : emptyHint('Эмбедов пока нет'),
        addButton('Эмбед', () => store.change(() => d.embeds.push(newEmbed())), { disabled: d.embeds.length >= LIMITS.embedsPerMessage }),
      ],
    }),
    sectionPanel({
      id: 'sec-rows',
      title: 'Кнопки и меню',
      icon: 'mouse',
      badge: `${d.rows.length}/${LIMITS.actionRows}`,
      defaultCollapsed: d.rows.length === 0,
      body: () => [
        d.rows.length ? h('div', { class: 'list' }, d.rows.map((r, i) => rowEditor(r, `Ряд ${i + 1}`, listActions(d.rows, i, (x) => cloneWithNewIds(x))))) : emptyHint('Кнопки-ссылки, выдача ролей и ответы шаблонами'),
        addButton('Ряд компонентов', () => store.change(() => d.rows.push(newRow())), { disabled: d.rows.length >= LIMITS.actionRows }),
      ],
    }),
    pollSection(),
  ];
}

function pollSection(): HTMLElement {
  const d = store.draft;
  const p = d.poll;
  return sectionPanel({
    id: 'sec-poll',
    title: 'Опрос',
    icon: 'poll',
    badge: p ? `${p.answers.length} отв.` : undefined,
    defaultCollapsed: !p,
    extra: [
      p
        ? h('button', { class: 'btn btn-sm btn-ghost', type: 'button', onclick: () => store.change((x) => (x.poll = null)) }, icon('trash', 14), 'Убрать')
        : h('button', { class: 'btn btn-sm', type: 'button', onclick: () => store.change((x) => (x.poll = newPoll())) }, icon('plus', 14), 'Добавить'),
    ],
    body: () =>
      !p
        ? [emptyHint('Опрос до 10 ответов, от 1 часа до 32 дней. После отправки его нельзя изменить.')]
        : [
            textField({ label: 'Вопрос', value: p.question, max: LIMITS.pollQuestion, required: true, onInput: (v) => store.input('poll.q', () => (p.question = v)) }),
            group(
              `Ответы (${p.answers.length}/${LIMITS.pollAnswers})`,
              h(
                'div',
                { class: 'nested-list' },
                p.answers.map((a, i) =>
                  h(
                    'div',
                    { class: 'poll-answer-row', dataset: { id: a.id } },
                    textField({ value: a.text, max: LIMITS.pollAnswerText, placeholder: `Ответ ${i + 1}`, label: `Ответ ${i + 1}`, onInput: (v) => store.input(`${a.id}.t`, () => (a.text = v)) }),
                    emojiField('Эмодзи', a.emoji, (v) => store.input(`${a.id}.e`, () => (a.emoji = v))),
                    h('button', { class: 'icon-btn danger', type: 'button', title: 'Удалить ответ', onclick: () => store.change(() => p.answers.splice(i, 1)) }, icon('trash', 15)),
                  ),
                ),
              ),
              addButton('Ответ', () => store.change(() => p.answers.push({ id: uid(), text: '', emoji: '' })), { small: true, disabled: p.answers.length >= LIMITS.pollAnswers }),
            ),
            row(
              selectField(
                'Длительность',
                String(p.durationHours),
                [
                  { value: '1', label: '1 час' },
                  { value: '4', label: '4 часа' },
                  { value: '8', label: '8 часов' },
                  { value: '24', label: '24 часа' },
                  { value: '72', label: '3 дня' },
                  { value: '168', label: '1 неделя' },
                  { value: '336', label: '2 недели' },
                  { value: '768', label: '32 дня' },
                  ...([1, 4, 8, 24, 72, 168, 336, 768].includes(p.durationHours) ? [] : [{ value: String(p.durationHours), label: `${p.durationHours} ч` }]),
                ],
                (v) => store.input('poll.dur', () => (p.durationHours = Number(v))),
              ),
              numberField('Или точно', p.durationHours, 1, LIMITS.pollMaxHours, (v) => store.input('poll.dur', () => (p.durationHours = v)), 'ч'),
            ),
            switchField('Можно выбрать несколько ответов', p.multiselect, (v) => store.input('poll.multi', () => (p.multiselect = v))),
          ],
  });
}
